import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Heading from '@athena/spark/Heading';
import InlineAlert from '@athena/spark/InlineAlert';
import cx from 'classnames';
import { useAppState } from 'src/components/provider/AppStateProvider';
import { useAmplitude } from 'src/lib/amplitude';
import { AmplitudeButton } from 'src/components/container/AmplitudeButton';
import Container from 'src/components/ui/Container';
import { RoutePaths } from 'src/workflow/routeMap';
import styles from './InvalidAppointmentPage.module.scss';

// A wrapper component for forge Heading is necessary to enable translation of the heading text
const ErrorHeading = (props: any) => {
  const { children } = props;
  return (
    <Heading tabIndex={0} className={styles.heading} tag="h1" variant="page">
      {children[0]}
    </Heading>
  );
};

const InvalidAppointmentPage: React.FC = () => {
  const { t } = useTranslation();
  const { arrivedViaDeeplink } = useAppState();
  const pageName = (arrivedViaDeeplink && 'InvalidDeeplinkAppointment') || '';
  const { ampTrackPageView } = useAmplitude(pageName);
  const navigate = useNavigate();

  const title = t('TITLE_INVALID_VISIT_LINK');
  document.title = title;

  useEffect(() => {
    ampTrackPageView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container className={styles.container}>
      <div className={styles.errorImage} />
      {arrivedViaDeeplink ? (
        <div>
          <Heading className={styles.heading}>{t(`INVALID_DEEPLINK_TITLE`)}</Heading>
          <div className={cx(styles.helpText, 'fe_u_margin--bottom-xlarge')}>{t(`INVALID_DEEPLINK_CONTENT`)}</div>
        </div>
      ) : (
        <div>
          <Trans i18nKey={'INVALID_APPT_PAGE'}>
            <ErrorHeading>Oops, something went wrong on our end</ErrorHeading>
            <div tabIndex={0} className={styles.helpText}>
              Please try to refresh your browser or contact your practice.
              <br role="presentation" />
              <br role="presentation" />
              Thank you for your patience as we resolve technical issues.
            </div>
          </Trans>
          <AmplitudeButton
            action="GoToDeviceCheck"
            size="large"
            fullWidth
            className={styles.deviceCheckBtn}
            variant="outline"
            onClick={() => navigate(RoutePaths.DeviceCheck)}
            aria-label={t('INVALID_APPT_PAGE_START_DEVICE_CHECK')}
          >
            {t('INVALID_APPT_PAGE_START_DEVICE_CHECK')}
          </AmplitudeButton>
        </div>
      )}

      <InlineAlert type="info">
        <span tabIndex={0} className={styles.note}>
          {t('INVALID_APPT_PAGE_MESSAGE_IN_ERROR')}
        </span>
      </InlineAlert>
    </Container>
  );
};

export default InvalidAppointmentPage;
